import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import Modal from "../../../layouts/Modal";
import { NumberInput } from "../Inputs/NumberInput";
import { ButtonInput } from "../Inputs/ButtonInput";
import CompareBadge from "./CompareBadge";


type FormValues = {
  amendedPrice: number | string;
};

const formatGBP = (n?: number | null) =>
  n == null || Number.isNaN(n) ? "—" : `£${n.toFixed(2)}`;

export default function AmendPriceModal({
  open,
  setOpen,
  initialValue,
  collectionPrice,
  dropOffPrice,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  initialValue?: number | null;
  collectionPrice?: number | null;
  dropOffPrice?: number | null;
  onConfirm: (amendedPrice: number) => void;
  isLoading?: boolean;
}) {
  const {
    handleSubmit,
    register,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { amendedPrice: "" },
  });

  useEffect(() => {
    if (!open) return;
    reset({ amendedPrice: initialValue ?? "" });
  }, [open, initialValue, reset]);

  const raw = watch("amendedPrice");
  const amendedNumber = useMemo(() => {
    const n = typeof raw === "string" ? Number(raw) : raw;
    return Number.isFinite(n) ? n : null;
  }, [raw]);

  const isBusy = !!isLoading || isSubmitting;
  const isValidNumber = amendedNumber != null && amendedNumber >= 0;

  const isUsablePrice = (n?: number | null) => n != null && Number.isFinite(n) && n > 0;

  const safeCollection = isUsablePrice(collectionPrice) ? collectionPrice : null;
  const safeDropOff = isUsablePrice(dropOffPrice) ? dropOffPrice : null;

  const amendedUsable = amendedNumber != null && amendedNumber > 0;

  const onSubmit = (data: FormValues) => {
    const value =
      typeof data.amendedPrice === "string"
        ? Number(data.amendedPrice)
        : data.amendedPrice;

    if (!Number.isFinite(value) || value < 0) return;
    onConfirm(value);
  };

  return (
    <Modal width="max-w-lg" open={open} setOpen={setOpen}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Amend price</h1>
          <p className="mt-1 text-sm text-gray-600">
            Set an amended price to override the quote total.
          </p>
        </div>

        {/* Summary box */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-2 text-sm items-center">
            {/* Collection */}
            <span className="text-gray-600">Collection</span>
            <span className={`font-medium tabular-nums text-right ${safeCollection ? "text-gray-900" : "text-gray-400"}`}>
              {safeCollection ? formatGBP(safeCollection) : "—"}
            </span>
            <div className="flex justify-end">
              <CompareBadge base={safeCollection} amended={amendedUsable ? amendedNumber : null} compact />
            </div>

            {/* Drop Off */}
            <span className="text-gray-600">Drop Off</span>
            <span className={`font-medium tabular-nums text-right ${safeDropOff ? "text-gray-900" : "text-gray-400"}`}>
              {safeDropOff ? formatGBP(safeDropOff) : "—"}
            </span>
            <div className="flex justify-end">
              <CompareBadge
                base={safeDropOff}
                amended={amendedUsable ? amendedNumber : null}
                compact
              />
            </div>

            {/* Divider */}
            <div className="col-span-3 my-1 h-px bg-gray-200" />

            {/* Amended preview */}
            <span className="text-gray-600">Amended</span>
            <span className="font-semibold text-gray-900 tabular-nums text-right">
              {isValidNumber ? formatGBP(amendedNumber) : "—"}
            </span>
            <span />
          </div>

          {/* Input */}
          <div className="pt-4">
            <NumberInput
              label="Amended Price"
              register={register}
              registerName="amendedPrice"
              step=".01"
              minVal={0}
              inputClasses="px-3 py-2"
            />

            {!isValidNumber && String(raw ?? "").trim() !== "" && (
              <p className="mt-2 text-sm text-red-600">
                Please enter a valid number (greater than 0).
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-3 pt-1">
          <ButtonInput
            label={"Cancel"}
            isSubmit={false}
            onClick={() => setOpen(false)}
            disabled={isBusy}
            classes="!border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-100"
          />

          <ButtonInput
            label={isBusy ? "Saving..." : "Save amended price"}
            isSubmit
            disabled={isBusy || !isValidNumber}
          />
        </div>
      </form>
    </Modal>
  );
}